import { useState } from 'react';
import { BOOLEAN, CREATE_FIELD, DATA_TYPE, DECIMAL, ENTER_NAME, TEXT_FIELD, FIELD_NAME, FRACTION, INTEGER, MULTI_CHOICE, CALCULATED, FILTER, CALCULATED_TOOL_TIP, FILTER_TOOL_TIP, RANGE_FIELD } from '../ISRConstants';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Button from 'Components/DesignSystem/Button/Button';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';

type TFieldProps = {
    id: string;
    onClose: () => void;
    onFieldSubmit: (fieldName: string, fieldType: string, calculated: boolean, filter: boolean) => boolean | void;
    multiCreate?: boolean;
    entityName?: string;
}

// Filter toggle is hidden in the Create Field dialog until the feature is ready.
// Set to false to re-enable it. Filter config remains available from the field panel after creation.
const HIDE_FILTER_IN_CREATE_DIALOG = true;

/**
 * The Create Field prompt, on DS Dialog + TextField + Dropdown + Toggle + Buttons.
 *
 * **Why this one mattered.** Its sibling `CreateISRDialog` (Create Sub model) moved
 * to a DS Dialog in § 88 while this stayed a hand-rolled `tw-fixed tw-inset-0`
 * overlay with no focus trap, no Escape and no `aria-modal`. The two buttons that
 * open them sit inches apart in the Model Explorer, so one screen shipped two
 * different dialog behaviours. Escape and scrim-click now close this one too.
 *
 * **The empty "Select" option is deliberate.** A DS Dropdown placeholder is not
 * selectable, and the native `<select>` this replaces let you choose "Select" to
 * clear a chosen type again. Keeping it as a real option with an empty value
 * preserves that; `CreateFieldDialog.test.tsx` pins it.
 */
const CreateFieldDialog = ({ id, onClose, onFieldSubmit, multiCreate = false, entityName = 'Field' }: TFieldProps) => {
    const [fieldName, setFieldName] = useState("");
    const [fieldType, setFieldType] = useState("");
    const [calculated, setCalculated] = useState<boolean>(false);
    const [filter, setFilter] = useState<boolean>(false);
    const [justCreated, setJustCreated] = useState(false);

    const dataTypeOptions = [
        { value: '', label: 'Select' },
        ...[TEXT_FIELD, INTEGER, DECIMAL, FRACTION, BOOLEAN, MULTI_CHOICE, RANGE_FIELD].map((item) => ({
            value: item,
            label: item,
        })),
    ];

    const resetForm = () => {
        setFieldName("");
        setCalculated(false);
        setFilter(false);
    };

    const handleCreateAndAddMore = () => {
        const result = onFieldSubmit(fieldName, fieldType, calculated, filter);
        if (result === false) return;
        resetForm();
        setJustCreated(true);
        setTimeout(() => setJustCreated(false), 2000);
    };

    const handleCreate = () => {
        const result = onFieldSubmit(fieldName, fieldType, calculated, filter);
        if (result === false) return;
        toast.success(`${entityName} created successfully`);
        onClose();
    };

    const handleSingleCreate = () => {
        onFieldSubmit(fieldName, fieldType, calculated, filter);
        resetForm();
    };

    const handleFieldTypeChange = (value: string | number) => {
        setFieldType(String(value));
    };

    const handleCalculatedChange = (value: boolean) => {
        setCalculated(value);
    };

    const handleFilterChange = (value: boolean) => {
        setFilter(value);
    };

    const isFormValid = !!fieldName && !!fieldType;

    const cancelButton = (
        <Button
            id={`${id}-cancel-btn`} data-testid={`${id}-cancel-btn`}
            variant="secondary"
            onClick={onClose}
        >
            Cancel
        </Button>
    );

    /* Cancel sits at the far LEFT, the create actions grouped right. The dismissive
       action and the committing ones are different kinds of thing, and separating
       them costs a mis-click far less often than a row where Cancel abuts Create. */
    const footer = (
        <div id={`${id}-actions`} className='tw-flex tw-items-center tw-justify-between'>
            {cancelButton}
            <div className='tw-flex tw-gap-2'>
            {multiCreate ? (
                <>
                    {justCreated ? (
                        /* The transient "✓ Created" flash, kept verbatim from the legacy
                           markup for the same reason as CreateISRDialog's: a green-filled
                           disabled control has no DS Button equivalent. */
                        <button data-testid="components-createfielddialog-button-3"
                            id={`${id}-add-more-btn`}
                            disabled
                            className='tw-flex tw-items-center tw-gap-1 tw-bg-[#36B37E] tw-text-white tw-rounded tw-px-3 tw-h-6 tw-text-xs tw-font-medium tw-border-0 tw-cursor-default tw-opacity-100'
                        >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5.5" stroke="white" strokeWidth="1"/><path d="M3 6L5 8.5L9 3.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            {entityName} Created
                        </button>
                    ) : (
                        <Button data-testid="components-createfielddialog-button-4"
                            id={`${id}-add-more-btn`}
                            variant="secondary"
                            disabled={!isFormValid}
                            onClick={handleCreateAndAddMore}
                        >
                            Create &amp; Add More
                        </Button>
                    )}
                    <Button
                        id={`${id}-submit-btn`} data-testid={`${id}-submit-btn`}
                        variant="primary"
                        disabled={!isFormValid}
                        onClick={handleCreate}
                    >
                        Create
                    </Button>
                </>
            ) : (
                <Button
                    id={`${id}-submit-btn`} data-testid={`${id}-submit-btn`}
                    variant="primary"
                    disabled={!isFormValid}
                    onClick={handleSingleCreate}
                >
                    Create
                </Button>
            )}
            </div>
        </div>
    );

    return (
        <Dialog isOpen onClose={onClose} title={CREATE_FIELD} data-testid={id} footer={footer}>
            <div id={`${id}-form-section`} className='tw-flex tw-items-start tw-gap-4'>
                <TextField
                    id={`${id}-name-input`}
                    data-testid="components-createfielddialog-input-1"
                    label={FIELD_NAME}
                    value={fieldName}
                    onChange={setFieldName}
                    placeholder={ENTER_NAME}
                    className='tw-flex-1'
                />
                <Dropdown
                    id={`${id}-type-select`}
                    data-testid="components-createfielddialog-select-2"
                    label={DATA_TYPE}
                    options={dataTypeOptions}
                    value={fieldType}
                    onChange={handleFieldTypeChange}
                    /* The dialog clips its own overflow; without a portal the menu is
                       cut off at the dialog's edge. */
                    usePortal
                    /* 28% of the row, the split this form had before the migration
                       (72/28). A fixed 200px was wider than the content needs — the
                       longest option, "Multi-choice", sits well inside it. */
                    className='tw-w-[28%]'
                />
            </div>
            <div id={`${id}-flags-section`} className='tw-flex tw-items-start tw-gap-8 tw-mt-4'>
                {/*
                    FieldTitle rather than Toggle's own label, because each of these
                    carries an info tooltip explaining what the flag does and Toggle
                    has no slot for one. The switch names itself with an explicit
                    `aria-label` matching the visible text — Toggle overwrites any
                    `aria-labelledby` a caller passes.
                */}
                <div id={`${id}-calculated-field`} className='tw-flex tw-flex-col tw-gap-2'>
                    <FieldTitle
                        label={CALCULATED}
                        infoMessage={CALCULATED_TOOL_TIP}
                        data-testid={`${id}-calculated-label`}
                    />
                    <Toggle
                        id={`${id}-calculated-toggle`}
                        data-testid={`${id}-calculated-toggle`}
                        label=""
                        aria-label={CALCULATED}
                        checked={calculated}
                        onChange={handleCalculatedChange}
                    />
                </div>
                {!HIDE_FILTER_IN_CREATE_DIALOG && (
                    <div id={`${id}-filter-field`} className='tw-flex tw-flex-col tw-gap-2'>
                        <FieldTitle
                            label={FILTER}
                            infoMessage={FILTER_TOOL_TIP}
                            data-testid={`${id}-filter-label`}
                        />
                        <Toggle
                            id={`${id}-filter-toggle`}
                            data-testid={`${id}-filter-toggle`}
                            label=""
                            aria-label={FILTER}
                            checked={filter}
                            onChange={handleFilterChange}
                        />
                    </div>
                )}
            </div>
        </Dialog>
    );
}

export default CreateFieldDialog;
